import React, { useMemo, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import * as actionTypes from "./actionTypes";
import tasksReducer, { reducerInitialState } from "./tasksReducer";
import { apolloClient } from "@td/ui";
import CARE_TEAM_TASKS_QUERY from "../../queries/care-team-tasks.graphql";
import { useCategories } from "../categories-context";
import { pick, keys, isEmpty, isEqual } from "lodash";
import { parseQueryString, applyQueryString } from "../../utils/urls";
import {
  filterTypes,
  defaultQueryParams,
  defaultSortingParams,
  defaultFilters,
} from "../../constants";
import {
  mapFiltersToQuery,
  mapFiltersToQueryString,
  mapQueryStringToFilters,
  mapStoredFiltersToFilters
} from "../../utils/filters";

const TasksContext = React.createContext();

const TasksProvider = ({ defaultView, ...props }) => {
  const {
    filters: storedFilters,
    perPage: storedPerPage,
    sorting: storedSortingParams,
  } = defaultView;

  const categories = useCategories();
  const parsedQueryString = parseQueryString();

  const initialState = useMemo(() => ({
    ...reducerInitialState,
    queryParams: {
      ...defaultQueryParams,

      ...(storedPerPage && ({
        ...defaultQueryParams,
        perPage: storedPerPage,
      })),

      ...(!isEmpty(parsedQueryString) && ({
        ...defaultQueryParams,
        ...pick(parsedQueryString, keys(defaultQueryParams)),
      })),
    },
    sortingParams: {
      ...defaultSortingParams,

      ...(storedSortingParams && ({
        ...defaultSortingParams,
        ...storedSortingParams,
      })),

      ...(!isEmpty(parsedQueryString) && ({
        ...defaultSortingParams,
        ...pick(parsedQueryString, keys(defaultSortingParams)),
      })),
    },
    filters: {
      ...defaultFilters,
      [filterTypes.CATEGORY]: Object.values(categories)[0],

      ...(storedFilters && ({
        ...defaultFilters,
        [filterTypes.CATEGORY]: null,
        [filterTypes.STATUSES]: [],
        ...mapStoredFiltersToFilters(storedFilters),
      })),

      ...(!isEmpty(parsedQueryString) && ({
        ...defaultFilters,
        [filterTypes.CATEGORY]: null,
        [filterTypes.STATUSES]: [],
        ...pick(
          mapQueryStringToFilters(parsedQueryString),
          keys(defaultFilters)
        ),
      })),
    },
  }), []);

  const [state, dispatch] = React.useReducer(tasksReducer, initialState);

  const fetchTasks = useCallback(() => {
    const { queryParams, sortingParams, filters } = state;

    dispatch({ type: actionTypes.FETCH_TASKS_PENDING });

    apolloClient.query({
      query: CARE_TEAM_TASKS_QUERY,
      variables: {
        ...queryParams,
        sortingParams,
        filteringParams: mapFiltersToQuery(filters),
      },
      fetchPolicy: "network-only",
    }).then(({ data: { careTeamTasks: { items: tasks, page, perPage, totalCount: total }}}) => {
      dispatch({
        type: actionTypes.FETCH_TASKS_SUCCEEDED,
        payload: { tasks, meta: { page, perPage, total }},
      });
    }).catch((error) => {
      dispatch({
        type: actionTypes.FETCH_TASKS_FAILED,
        payload: { error },
      });
    });

    const preparedQueryString = {
      ...mapFiltersToQueryString(filters),
      ...sortingParams,
      ...queryParams,
    };

    if (!isEqual(parsedQueryString, preparedQueryString)) {
      applyQueryString(preparedQueryString);
    }
  }, [state, dispatch]);

  const updateQueryParams = useCallback((payload) => {
    dispatch({ type: actionTypes.UPDATE_QUERY_PARAMS, payload });
  }, [dispatch]);

  const updateSortingParams = useCallback((payload) => {
    dispatch({ type: actionTypes.UPDATE_SORTING_PARAMS, payload });
  }, [dispatch]);

  const initializeFilters = useCallback((payload) => {
    dispatch({ type: actionTypes.INITIALIZE_FILTERS, payload });
  }, [dispatch]);

  const updateFilters = useCallback((payload) => {
    dispatch({ type: actionTypes.UPDATE_FILTERS, payload });
  }, [dispatch]);

  const resetFilters = useCallback(() => {
    dispatch({ type: actionTypes.RESET_FILTERS });
  }, [dispatch]);

  const value = {
    ...state,
    fetchTasks,
    updateQueryParams,
    updateSortingParams,
    initializeFilters,
    updateFilters,
    resetFilters,
  };

  return (
    <TasksContext.Provider
      {...props}
      value={value}
    />
  );
};

TasksProvider.propTypes = {
  defaultView: PropTypes.object.isRequired,
};

export const useTasks = () => {
  const context = useContext(TasksContext);

  if (context === undefined) {
    throw new Error("useTasks must be used within a TasksProvider");
  }

  return context;
};

export default TasksProvider;
